const GIFT_PROPERTY = '_curtain_gift';

export function isGift(item, config) {
  return item.properties?.[GIFT_PROPERTY] === 'bag' || Boolean(config?.productId && String(item.product_id) === String(config.productId));
}

export function curtainQuantity(cart, config) {
  const curtainIds = new Set(cart.curtain_product_ids.map(String));
  return cart.items.reduce((total, item) => {
    if (isGift(item) || String(item.product_id) === String(config.productId) || !curtainIds.has(String(item.product_id))) return total;
    const panels = config.countPanels ? Number(item.product_title.match(/set of\s+(\d+)/i)?.[1] || 1) : 1;
    return total + item.quantity * panels;
  }, 0);
}

export function giftPlan(cart, config) {
  const quantity = curtainQuantity(cart, config);
  const eligible = config.enabled && config.available && quantity >= 2 && (config.price === 0 || config.discountReady);
  const updates = {};
  let keptGift = false;
  for (const item of cart.items.filter(item => isGift(item, config))) {
    const keep = eligible && !keptGift && String(item.variant_id) === String(config.variantId);
    const desiredQuantity = keep ? 1 : 0;
    if (item.quantity !== desiredQuantity) updates[item.key] = desiredQuantity;
    if (keep) keptGift = true;
  }
  return { quantity, updates, add: eligible && !keptGift };
}

export function createGiftController(config, dependencies) {
  const { readCart, updateCart, addGift, render } = dependencies;
  let pending;
  let dirty = false;
  let unavailable = false;
  let snapshot;

  function sync() {
    dirty = true;
    if (pending) return pending;
    pending = (async () => {
      while (dirty) {
        dirty = false;
        let cart = await readCart();
        const plan = giftPlan(cart, unavailable ? { ...config, available: false } : config);
        let changed = false;
        if (Object.keys(plan.updates).length) {
          await updateCart(plan.updates);
          changed = true;
        }
        if (plan.add) {
          try {
            await addGift(config.variantId);
            changed = true;
          } catch (error) {
            unavailable = true;
            console.warn('Complimentary bag could not be added:', error);
          }
        }
        if (changed) cart = await readCart();
        // Shopify is authoritative: never leave a chargeable promotional bag in the cart.
        const chargeableGifts = cart.items.filter(item => isGift(item, config) && item.final_line_price > 0);
        if (chargeableGifts.length) {
          await updateCart(Object.fromEntries(chargeableGifts.map(item => [item.key, 0])));
          cart = await readCart();
          unavailable = true;
          changed = true;
        }
        snapshot = cart;
        await render(cart, { changed, unavailable });
      }
      return snapshot;
    })().finally(() => { pending = null; });
    return pending;
  }
  return { sync };
}

function initialize() {
  const configNode = document.getElementById('CurtainGiftConfig');
  if (!configNode || window.CurtainGift) return;
  const config = JSON.parse(configNode.textContent);
  const nativeFetch = window.fetch.bind(window);
  const pendingWrites = new Set();
  let timer;
  let currentCart;
  let preparingCheckout = false;
  const root = config.root.endsWith('/') ? config.root : `${config.root}/`;

  const cartWrite = (input, init) => {
    const url = new URL(typeof input === 'string' || input instanceof URL ? input : input.url, location.origin);
    const method = init?.method || input?.method || 'GET';
    return url.origin === location.origin && method.toUpperCase() === 'POST' && /\/cart\/(add|change|update|clear)(\.js)?$/.test(url.pathname);
  };

  async function readCart() {
    await Promise.allSettled([...pendingWrites]);
    const response = await nativeFetch(`${config.cartUrl}?view=curtain-gift`, { cache: 'no-store' });
    if (!response.ok) throw new Error('Could not check the complimentary bag');
    const data = await response.json();
    if (!Array.isArray(data.cart?.items) || !Array.isArray(data.curtain_product_ids)) throw new Error('Invalid cart snapshot');
    return { ...data.cart, curtain_product_ids: data.curtain_product_ids };
  }

  async function postCart(action, body) {
    const response = await nativeFetch(`${root}cart/${action}.js`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body)
    });
    if (!response.ok) throw new Error('The complimentary bag is unavailable');
    return response.json();
  }

  function updateCards() {
    if (!currentCart) return;
    document.querySelectorAll('#cart-info .ajaxcart__product').forEach(row => {
      const key = row.querySelector('[data-id]')?.dataset.id;
      const item = currentCart.items.find(line => line.key === key);
      const managed = item && isGift(item, config);
      row.classList.toggle('curtain-gift-line', Boolean(managed));
      row.classList.toggle('curtain-gift-item', Boolean(managed));
      const lineTemplate = document.getElementById('CurtainGiftLine');
      if (managed && !row.querySelector('.curtain-gift-item__copy') && lineTemplate) {
        row.replaceChildren(lineTemplate.content.cloneNode(true));
      }
      if (managed) {
        const quantityInput = row.querySelector('input[name="updates[]"]');
        if (quantityInput) {
          quantityInput.dataset.id = item.key;
          quantityInput.value = item.quantity;
        }
      }
      if (managed && item.final_line_price === 0) {
        const price = row.querySelector('.product-price');
        if (price && price.textContent !== 'Free') price.textContent = 'Free';
      }
    });
  }

  const controller = createGiftController(config, {
    readCart,
    updateCart: updates => postCart('update', { updates }),
    addGift: id => postCart('add', { items: [{ id, quantity: 1, properties: { [GIFT_PROPERTY]: 'bag' } }] }),
    async render(cart, state) {
      currentCart = cart;
      updateCards();
      if (!state.changed) return;
      if (location.pathname.replace(/\/$/, '') === config.cartUrl.replace(/\/$/, '')) {
        if (!preparingCheckout) location.reload();
        return;
      }
      const response = await nativeFetch(`${config.cartUrl}?view=json`, { cache: 'no-store' });
      if (!response.ok) throw new Error('Could not refresh the shopping bag');
      const html = await response.text();
      const drawer = document.getElementById('cart-info');
      if (drawer) drawer.innerHTML = html;
      document.querySelectorAll('#CartCount, #CartCountCavas').forEach(node => { node.textContent = cart.item_count; });
      if (window.Shopify?.formatMoney) {
        const total = document.getElementById('cart_total');
        if (total) total.innerHTML = window.Shopify.formatMoney(cart.total_price, window.theme?.moneyFormat);
      }
      updateCards();
      document.dispatchEvent(new CustomEvent('curtain-gift:updated', { detail: { cart } }));
    }
  });
  window.CurtainGift = {
    sync: controller.sync,
    async prepareCheckout() {
      preparingCheckout = true;
      try { return await controller.sync(); }
      finally { preparingCheckout = false; }
    }
  };

  function schedule() {
    clearTimeout(timer);
    timer = setTimeout(() => controller.sync().catch(error => {
      console.warn('Could not update the complimentary bag:', error);
    }), 100);
  }

  // Observe both cart transports used by the theme; gift requests use nativeFetch to avoid recursion.
  window.fetch = function(input, init) {
    const request = nativeFetch(input, init);
    if (cartWrite(input, init)) {
      pendingWrites.add(request);
      request.then(response => {
        pendingWrites.delete(request);
        if (response.ok) schedule();
      }, () => { pendingWrites.delete(request); });
    }
    return request;
  };
  if (window.jQuery) {
    window.jQuery(document).on('ajaxSend.curtainGift', (_event, xhr, settings) => {
      if (!cartWrite(settings.url, { method: settings.type })) return;
      pendingWrites.add(xhr);
      xhr.always(() => {
        pendingWrites.delete(xhr);
        if (xhr.status >= 200 && xhr.status < 300) schedule();
      });
    });
  }

  // Normalize gift rows after each legacy drawer replacement.
  const drawer = document.getElementById('cart-info');
  if (drawer) new MutationObserver(updateCards).observe(drawer, { childList: true, subtree: true });
  window.addEventListener('pageshow', schedule);
  document.addEventListener('visibilitychange', () => { if (!document.hidden) schedule(); });

  // Native checkout also waits for reconciliation. GoKwik uses the same sync in its preflight.
  const replayingForms = new WeakSet();
  document.addEventListener('submit', event => {
    const form = event.target;
    if (replayingForms.has(form) || !form.matches('form[action*="/cart"]') || event.submitter?.name !== 'checkout') return;
    event.preventDefault();
    event.stopImmediatePropagation();
    window.CurtainGift.prepareCheckout().then(() => {
      const currentForm = form.isConnected ? form : document.querySelector('#cart-info form[action*="/cart"]');
      if (!currentForm) {
        location.href = `${root}checkout`;
        return;
      }
      const submitter = currentForm === form ? event.submitter : currentForm.querySelector('[name="checkout"]');
      replayingForms.add(currentForm);
      currentForm.requestSubmit(submitter || undefined);
      replayingForms.delete(currentForm);
    }).catch(() => {
      document.querySelectorAll('[data-curtain-gift-status]').forEach(node => {
        node.textContent = 'Your shopping bag is still updating. Please try again.';
      });
    });
  }, true);
  schedule();
}

if (typeof window !== 'undefined') {
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, { once: true });
  else initialize();
}
